import type { GridColumn } from '@glideapps/glide-data-grid'
import useStateFromProps from '@hooks/useStateFromProps'
import { addAtIndex, removeAtIndex } from '@utils/lists'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type { AvailableColumn } from '../types'
import {
  areCategoriesLoaded,
  extractSelectedNodesFromCategories,
  findDynamicColumn,
  isValidColumn,
  mergeSelectedColumns,
  sortColumns,
} from './utils'
import { visibleWithPersistence } from './withPersistence'

export interface ColumnCategory<Sort extends string = string> {
  name: string
  prefix: string
  children?: string[]
  hidden?: boolean
  availableNodes: Array<AvailableColumn<Sort>> | undefined
  selectedNodes: Array<AvailableColumn<Sort>> | undefined
  initialSearch?: string
  onSearch: (query: string) => void
  hasNextPage: boolean
  hasPreviousPage: boolean
  onNextPage: (query: string) => void
  onPreviousPage: (query: string) => void
}

interface UseColumnsProps<Sort extends string> {
  staticColumns: Array<AvailableColumn<Sort>>
  columnCategories?: Array<ColumnCategory<Sort>>
  selectedColumns: string[]
  onSave: (columns: string[]) => void
  columnWidths?: Record<string, number>
  onUpdateColumnWidths?: (widths: Record<string, number>) => void
}

export const useColumns = <Sort extends string = string>({
  staticColumns,
  selectedColumns: _selectedColumns,
  columnCategories,
  onSave: handleSave,
  columnWidths = {},
  onUpdateColumnWidths,
}: UseColumnsProps<Sort>) => {
  const [dynamicColumns, updateDynamicColumns] = useState<Array<AvailableColumn<Sort>> | null>(null)

  const selectedColumns = _selectedColumns

  // Dynamic columns are loaded from the API, thus they need to be updated
  // after query resolves with data. Then we also sort them by order of addition
  // by the user
  useEffect(() => {
    if (dynamicColumns === null && areCategoriesLoaded(columnCategories)) {
      queueMicrotask(() => {
        updateDynamicColumns(
          sortColumns(extractSelectedNodesFromCategories(columnCategories), selectedColumns),
        )
      })
    }
  }, [columnCategories, selectedColumns, dynamicColumns])

  const initialColumnsState = useMemo(
    () => mergeSelectedColumns({ dynamicColumns, selectedColumns, staticColumns }),
    [dynamicColumns, staticColumns, selectedColumns],
  )

  const [recentlyAddedColumn, setRecentlyAddedColumn] = useState<string | null>(null)
  const [visibleColumns, setVisibleColumns] =
    useStateFromProps<Array<AvailableColumn<Sort>>>(initialColumnsState)

  const onSave = (columnsIds: string[]) => {
    handleSave(columnsIds)
  }

  const persistColumnWidths = useCallback(
    (columns: Array<AvailableColumn<Sort>>) => {
      if (!onUpdateColumnWidths) {
        return
      }

      const newWidths: Record<string, number> = {}
      columns.forEach((column) => {
        if (column.id !== 'empty') {
          newWidths[column.id] = column.width
        }
      })
      onUpdateColumnWidths(newWidths)
    },
    [onUpdateColumnWidths],
  )

  const handleVisibleColumnsChange = useCallback(
    (
      currentColumns: Array<AvailableColumn<Sort> | undefined>,
      persistOrder = false,
    ): Array<AvailableColumn<Sort>> => {
      const validColumns = currentColumns.filter(isValidColumn)

      persistColumnWidths(validColumns)

      if (persistOrder) {
        const columnIds = validColumns.map((column) => column.id).filter((id) => id !== 'empty')
        handleSave(columnIds)
      }

      return validColumns
    },
    [persistColumnWidths, handleSave],
  )

  const onMove = useCallback(
    (startIndex: number, endIndex: number): void => {
      // When empty column prevent to rearrange it order
      if (visibleColumns[0]?.id === 'empty') {
        if (startIndex === 0) {
          return
        }

        // Keep empty column always at beginning
        if (endIndex === 0) {
          return setVisibleColumns((old) =>
            handleVisibleColumnsChange(
              addAtIndex(old[startIndex], removeAtIndex(old, startIndex), endIndex + 1),
              true,
            ),
          )
        }
      }

      setVisibleColumns((old) =>
        handleVisibleColumnsChange(
          addAtIndex(old[startIndex], removeAtIndex(old, startIndex), endIndex),
          true,
        ),
      )
    },
    [visibleColumns, setVisibleColumns, handleVisibleColumnsChange],
  )

  const onResize = useCallback(
    (column: GridColumn, newSize: number) => {
      if (column.id === 'empty') {
        return
      }

      return setVisibleColumns((prevColumns) =>
        handleVisibleColumnsChange(
          prevColumns.map((prevColumn) =>
            prevColumn && prevColumn.id === column.id
              ? { ...prevColumn, width: newSize }
              : prevColumn,
          ),
        ),
      )
    },
    [setVisibleColumns, handleVisibleColumnsChange],
  )

  const onToggle = (columnId: string) => {
    const isAdded = !selectedColumns.includes(columnId)
    const isDynamic = columnId.includes(':')

    if (!isDynamic) {
      if (isAdded) {
        onSave([...selectedColumns, columnId])
        setRecentlyAddedColumn(columnId)
      } else {
        onSave(selectedColumns.filter((id) => id !== columnId))
      }
    }

    if (isDynamic) {
      const [prefix, id] = columnId.split(':')
      const hiddenColumnPrefixes =
        columnCategories?.find((cat) => cat.prefix === prefix)?.children ?? []

      if (isAdded) {
        onSave([
          ...selectedColumns,
          ...hiddenColumnPrefixes.map((prefix) => `${prefix}:${id}`),
          columnId,
        ])
        setRecentlyAddedColumn(columnId)
        updateDynamicColumns((prevDynamicColumns) =>
          [
            ...(prevDynamicColumns ?? []),
            ...hiddenColumnPrefixes.map((prefix) =>
              findDynamicColumn(columnCategories, `${prefix}:${id}`),
            ),
            findDynamicColumn(columnCategories, columnId),
          ].filter(isValidColumn),
        )
      } else {
        updateDynamicColumns((prevDynamicColumns) =>
          (prevDynamicColumns ?? []).filter((column) => !column.id.includes(id)),
        )
        onSave(selectedColumns.filter((currentId) => !currentId.includes(id)))
      }
    }
  }

  const onClearRecentlyAddedColumn = () => setRecentlyAddedColumn(null)
  // Should be used only for special cases
  const onCustomUpdateVisible = setVisibleColumns
  const onResetDynamicToInitial = (customSelected?: string[]) => {
    if (areCategoriesLoaded(columnCategories)) {
      updateDynamicColumns(
        sortColumns(
          extractSelectedNodesFromCategories(columnCategories),
          customSelected ?? selectedColumns,
        ),
      )
    }
  }

  return {
    columnCategories,
    dynamicColumns,
    handlers: {
      onClearRecentlyAddedColumn,
      onCustomUpdateVisible,
      onMove,
      onResetDynamicToInitial,
      onResize,
      onToggle,
    },
    recentlyAddedColumn,
    selectedColumns,
    staticColumns,
    visibleColumns: visibleWithPersistence(visibleColumns, columnWidths),
  }
}
